import React, { useCallback, useState } from 'react';
import { useMutation } from '@apollo/client';
import Modal, { ModalBody, ModalFooter } from '../../../../components/Modal';
import { Flex, Text } from 'rebass';
import Button from '../../../../components/Button/Button';
import { Label } from '../../../../components/Form';
import useNotify from '../../../../hooks/useNotify';
import { gql } from '@/modes/waterSupplier/gql';
import { IdArrayInput } from '../../../../hooks/useTablePages';
import WaterHazardsPicker, {
  WaterHazard,
} from '../../../../components/WaterHazard/WaterHazardsPicker';
import ButtonToggle from '../../../../components/Button/ButtonToggle';

import type { HazardAnswer } from '../../../../components/WaterHazard/types';

type IsProtected = HazardAnswer;

const isProtectedOptions: { value: IsProtected; label: string }[] = [
  { value: 'YES', label: 'Yes' },
  { value: 'NO', label: 'No' },
  { value: 'UNKNOWN', label: 'Unknown' },
];

type BulkAddHazardModalProps = {
  waterServiceIds: IdArrayInput;
  onClose: () => void;
};

const BULK_ADD_HAZARDS = gql(`
  mutation AddHazardsToWaterServices(
    $waterServiceIds: [ID!]!
    $waterHazardIds: [ID!]!
    $isProtected: HazardAnswer
  ) {
    addHazardsToWaterServices(
      waterServiceIds: $waterServiceIds
      waterHazardIds: $waterHazardIds
      isProtected: $isProtected
    ) {
      id
      hazardProtectionStatus
    }
  }
`);

const BulkAddHazardModal: React.FC<BulkAddHazardModalProps> = ({
  waterServiceIds,
  onClose,
}) => {
  const [hazards, setHazards] = useState<WaterHazard[]>([]);
  const [isProtected, setIsProtected] = useState<IsProtected>('UNKNOWN');
  const { showError, showSuccess } = useNotify();

  const count = waterServiceIds.length;
  const serviceLabel =
    count === 1 ? '1 water service' : `${count} water services`;

  const onCompleted = useCallback(() => {
    showSuccess(`Hazard(s) added to ${serviceLabel}`);
    onClose();
  }, [serviceLabel, onClose]);

  const onError = useCallback((error: Error) => {
    showError('Error adding hazards', error);
  }, []);

  const [bulkAddHazards, { loading }] = useMutation(BULK_ADD_HAZARDS, {
    onCompleted,
    onError,
    refetchQueries: ['GetWaterServiceIds'],
  });

  const save = useCallback(() => {
    if (!hazards.length) {
      showError('Please select at least one hazard');
      return;
    }
    bulkAddHazards({
      variables: {
        waterServiceIds: waterServiceIds as string[],
        waterHazardIds: hazards.map((h) => h.id),
        isProtected: isProtected as any,
      },
    });
  }, [hazards, isProtected, waterServiceIds, bulkAddHazards]);

  return (
    <Modal title="Add Hazards" onClose={onClose}>
      <ModalBody>
        <Flex p={3} flexDirection="column" sx={{ gap: 3, minWidth: 360 }}>
          <Text variant="body1">
            Add hazards to {serviceLabel}. Existing hazard records will not be
            modified.
          </Text>
          <Flex flexDirection="column" sx={{ gap: 1 }}>
            <Label>Hazards</Label>
            <WaterHazardsPicker
              onChange={(selected) => {
                const arr = Array.isArray(selected)
                  ? [...selected]
                  : selected
                    ? [selected]
                    : [];
                setHazards(arr as WaterHazard[]);
              }}
            />
          </Flex>
          <Flex flexDirection="column" sx={{ gap: 1 }}>
            <Label>Protected?</Label>
            <ButtonToggle
              name="isProtected"
              value={isProtected}
              buttonOptions={isProtectedOptions.map((opt) => ({
                ...opt,
                onClick: () => setIsProtected(opt.value),
              }))}
            />
          </Flex>
        </Flex>
      </ModalBody>
      <ModalFooter>
        <Button icon="save" onClick={save} disabled={loading}>
          Add Hazards
        </Button>
        <Button variant="outline" icon="slash-circle" onClick={onClose}>
          Cancel
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default BulkAddHazardModal;
